export enum ShopifyErrorCode {
  Network = 'NETWORK_ERROR',
  GraphQL = 'GRAPHQL_ERROR',
  UserError = 'USER_ERROR',
  NotFound = 'NOT_FOUND',
  Unauthorized = 'UNAUTHORIZED',
  Configuration = 'CONFIGURATION_ERROR',
  Unknown = 'UNKNOWN_ERROR',
}

export interface ShopifyGraphQLErrorDetail {
  message: string;
  extensions?: Record<string, unknown>;
}

export class ShopifyApiError extends Error {
  readonly code: ShopifyErrorCode;
  readonly statusCode?: number;
  readonly graphQLErrors: ShopifyGraphQLErrorDetail[];
  readonly userErrors: Array<{ field?: string[] | null; message: string }>;

  constructor(
    message: string,
    options: {
      code?: ShopifyErrorCode;
      statusCode?: number;
      graphQLErrors?: ShopifyGraphQLErrorDetail[];
      userErrors?: Array<{ field?: string[] | null; message: string }>;
      cause?: unknown;
    } = {},
  ) {
    super(message, { cause: options.cause });
    this.name = 'ShopifyApiError';
    this.code = options.code ?? ShopifyErrorCode.Unknown;
    this.statusCode = options.statusCode;
    this.graphQLErrors = options.graphQLErrors ?? [];
    this.userErrors = options.userErrors ?? [];
  }

  static fromUnknown(error: unknown): ShopifyApiError {
    if (error instanceof ShopifyApiError) {
      return error;
    }

    if (error instanceof Error) {
      return new ShopifyApiError(error.message, {
        code: ShopifyErrorCode.Unknown,
        cause: error,
      });
    }

    return new ShopifyApiError('An unexpected Shopify API error occurred.', {
      code: ShopifyErrorCode.Unknown,
      cause: error,
    });
  }

  static network(message: string, statusCode?: number, cause?: unknown): ShopifyApiError {
    return new ShopifyApiError(message, {
      code: ShopifyErrorCode.Network,
      statusCode,
      cause,
    });
  }

  static graphQL(errors: ShopifyGraphQLErrorDetail[]): ShopifyApiError {
    return new ShopifyApiError(
      errors.map((error) => error.message).join(', ') || 'GraphQL request failed.',
      { code: ShopifyErrorCode.GraphQL, graphQLErrors: errors },
    );
  }

  static userErrors(
    userErrors: Array<{ field?: string[] | null; message: string }>,
    fallbackMessage = 'Shopify user error.',
  ): ShopifyApiError {
    return new ShopifyApiError(
      userErrors.map((error) => error.message).join(', ') || fallbackMessage,
      { code: ShopifyErrorCode.UserError, userErrors },
    );
  }

  static notFound(resource: string): ShopifyApiError {
    return new ShopifyApiError(`${resource} not found.`, { code: ShopifyErrorCode.NotFound });
  }

  static configuration(message: string): ShopifyApiError {
    return new ShopifyApiError(message, { code: ShopifyErrorCode.Configuration });
  }
}
